import { createDocument, createTheme, p, HeadingLevel } from '@skills/docx-export.jsx';
import { logoBlock } from '@generated/components/logoDocx';
import { rangeLabel, shortDate, type StaffReport } from '@generated/components/reportDocx';
import { money } from '@generated/components/pricing';

const theme = () => createTheme({ primary: '1F2937', secondary: 'B91C1C', accent: '1D4ED8', neutral: '222222', font: 'Georgia' });
const lines = (s: string) => s.split(/\n+/).map((l) => l.trim()).filter(Boolean);
const prepared = () => `Prepared ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
const meta = (t: string) => p(t, { size: 16, color: '777777' });

/** Classifieds, oldest first, grouped by category — ad text ready to set in the paper. */
export function buildClassifiedsDocx(r: StaffReport) {
  const c: unknown[] = [...logoBlock(), p('Classified Ads', { heading: HeadingLevel.TITLE }), p(rangeLabel(r), { bold: true }), p(`${r.classifieds.length} ads · ${prepared()}`, { color: '777777' })];
  if (!r.classifieds.length) c.push(p('No classified ads were placed in these dates.', { italic: true }));
  const cats = [...new Set(r.classifieds.map((a) => a.category))].sort();
  cats.forEach((cat) => {
    const ads = r.classifieds.filter((a) => a.category === cat);
    c.push(p(`${cat} (${ads.length})`, { heading: HeadingLevel.HEADING_1 }));
    ads.forEach((a) => {
      lines(a.adText).forEach((l) => c.push(p(l)));
      c.push(meta(`${shortDate(a.createdAt)} · ${a.publication} · ${a.issueMonth} issue · ${a.name} · ${a.email}${a.phone ? ` · ${a.phone}` : ''} · ${money(a.amount)} ${a.paymentMethod} · ${a.status}`));
    });
  });
  const TITLE = 'Classified Ads';
  const t = theme();
  return createDocument({ title: TITLE, headerText: `Star-Revue · ${rangeLabel(r)}`, styles: t.styles, numbering: t.numbering, children: c });
}

/** Letters to the editor, oldest first, with the writer's name for print. */
export function buildLettersDocx(r: StaffReport) {
  const c: unknown[] = [...logoBlock(), p('Letters to the Editor', { heading: HeadingLevel.TITLE }), p(rangeLabel(r), { bold: true }), p(`${r.letters.length} letters · ${prepared()}`, { color: '777777' })];
  if (!r.letters.length) c.push(p('No letters arrived in these dates.', { italic: true }));
  r.letters.forEach((l) => {
    const subject = l.name.replace(/^Letter to the Editor:\s*/i, '').trim();
    c.push(p(subject || 'Letter', { heading: HeadingLevel.HEADING_1 }));
    lines((l.message ?? '').replace(/^LETTER TO THE EDITOR\s*\n?(From:.*\n)?/i, '')).forEach((t) => c.push(p(t)));
    c.push(p(`— ${l.anonymous ? 'Name withheld' : l.senderName || 'Name not given'}`, { italic: true }));
    c.push(meta(`Received ${shortDate(l.received)} · ${l.paper ?? ''}${l.anonymous ? '' : [l.email?.email, l.phone?.phone].filter(Boolean).map((x) => ` · ${x}`).join('')} · ${l.status ?? ''}`));
  });
  const TITLE = 'Letters to the Editor';
  const t = theme();
  return createDocument({ title: TITLE, headerText: `Star-Revue · ${rangeLabel(r)}`, styles: t.styles, numbering: t.numbering, children: c });
}
